import { ArrowRight, Settings2 } from 'lucide-react'
import StatusBadge from './StatusBadge'

function initials(name) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

function assetUrl(path) {
  if (!path) return null
  if (/^(https?:)?\/\//.test(path) || path.startsWith('blob:') || path.startsWith('data:')) return path
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
  if (path.startsWith(`${base}/`)) return path
  return `${base}/${path.replace(/^\//, '')}`
}

function LogoBox({ item, logoSrc, size = 'md' }) {
  const sizeClass = size === 'sm' ? 'h-11 w-11' : 'h-14 w-14'
  const textClass = size === 'sm' ? 'text-xs' : 'text-sm'

  return (
    <div
      className={`flex ${sizeClass} shrink-0 items-center justify-center overflow-hidden rounded-[0.6rem] bg-white ring-1 ring-slate-200/60 dark:bg-white dark:ring-slate-300/60`}
    >
      {logoSrc ? (
        <img
          src={logoSrc}
          alt=""
          className="h-full w-full object-contain p-1"
          onError={(e) => { e.currentTarget.style.display = 'none' }}
        />
      ) : (
        <span className={`${textClass} font-bold tracking-tight`} style={{ color: item.accent }}>
          {initials(item.name)}
        </span>
      )}
    </div>
  )
}

export default function IntegrationCard({ item, view = 'grid', onAction }) {
  const logoSrc = assetUrl(item.logo)
  const isCompact = view === 'list'

  const actionDisabled = item.status === 'coming_soon' || item.status === 'inactive'

  if (isCompact) {
    return (
      <article
        className="group flex items-center gap-4 rounded-xl border border-slate-500 bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 hover:border-slate-400 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.12)] focus-within:ring-2 focus-within:ring-brand-500/20 dark:border-slate-600 dark:bg-slate-900 dark:hover:border-slate-500 dark:hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.35)] dark:focus-within:ring-brand-500/30"
      >
        <LogoBox item={item} logoSrc={logoSrc} size="sm" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.name}</h3>
          <p className="mt-0.5 line-clamp-1 text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
        </div>
        <StatusBadge status={item.status} compact />
        <button
          type="button"
          disabled={actionDisabled}
          onClick={() => onAction?.(item)}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-brand-600 transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-45 dark:text-brand-400 dark:hover:bg-brand-500/10"
        >
          {item.actionLabel}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </article>
    )
  }

  return (
    <article
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-500 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-[0_16px_40px_-20px_rgba(15,23,42,0.18)] focus-within:ring-2 focus-within:ring-brand-500/20 dark:border-slate-600 dark:bg-slate-900 dark:hover:border-slate-500 dark:hover:shadow-[0_16px_40px_-20px_rgba(0,0,0,0.4)] dark:focus-within:ring-brand-500/30"
    >
      <div className="absolute right-4 top-4">
        <StatusBadge status={item.status} compact />
      </div>

      <LogoBox item={item} logoSrc={logoSrc} />

      <h3 className="mt-3 text-[15px] font-semibold leading-snug text-slate-900 dark:text-white">{item.name}</h3>
      <p className="mt-1.5 line-clamp-2 flex-1 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
        {item.description}
      </p>

      <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4 dark:border-slate-700">
        {item.status === 'connected' && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
            Healthy
          </span>
        )}
        {item.status !== 'connected' && <span className="text-[11px] text-slate-400 dark:text-slate-500">—</span>}
        <button
          type="button"
          disabled={actionDisabled}
          onClick={() => onAction?.(item)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-800 ring-1 ring-slate-200 transition-all duration-200 hover:bg-blue-600 hover:text-white hover:ring-brand-600 disabled:pointer-events-none disabled:opacity-40 dark:bg-slate-700 dark:text-slate-200 dark:ring-slate-600 dark:hover:bg-brand-500 dark:hover:text-white dark:hover:ring-brand-500"
        >
          {item.status === 'connected' ? (
            <Settings2 className="h-3.5 w-3.5" aria-hidden />
          ) : null}
          {item.actionLabel}
        </button>
      </div>
    </article>
  )
}
